<template>
  <dialog
    ref="el"
    v-trap-focus
    :closedBy="closedBy"
    @keydown.esc="handleEscape"
  >
    <slot />
  </dialog>
</template>
<script setup lang="ts">
import { ClosedBy } from "@/helpers/types";
import { getDebugMode } from "@/helpers/helperFunctions";
import { trapFocus } from "@/helpers/trapFocus";
import { useEventListener } from "@vueuse/core";
import { onMounted, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    modal?: boolean;
    closedBy?: ClosedBy;
  }>(),
  {
    modal: true,
    closedBy: "any",
  }
);

const el = useTemplateRef<HTMLDialogElement>("el");

let closedByPolyfill = false;
useEventListener(
  el,
  "click",
  (evt) => closedByPolyfill && onBackdropClick(evt)
);

onMounted(async () => {
  // lazy load polyfill only if needed
  // see: https://github.com/GoogleChrome/dialog-polyfill/blob/5033aac1b74c44f36cde47be3d11f4756f3f8fda/index.js#L545
  if (!el.value.showModal) {
    const polyfill = await import("dialog-polyfill");
    polyfill.default.registerDialog(el.value);
  }

  if (props.modal) {
    el.value.showModal();
  } else {
    el.value.show();
  }
  el.value.focus();

  closedByPolyfill = !(
    "closedBy" in (window.HTMLDialogElement?.prototype ?? {})
  );
  if (closedByPolyfill && getDebugMode()) {
    console.warn(
      "Dialog.closedBy is not supported. Using custom polyfill instead ..."
    );
  }
});

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = el.value;

  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}

function onBackdropClick(event: MouseEvent | TouchEvent) {
  if (props.closedBy !== "any") {
    return;
  }
  const path = event.composedPath();
  const isFromAllowed = path.some(
    (el) =>
      el instanceof HTMLElement &&
      (el.classList?.contains("keep-dialog") || el.closest?.(".keep-dialog"))
  );

  // close the dialog if the click is outside an element marked with .keep-dialog class
  if (!isFromAllowed) {
    if (el.value && el.value.open) {
      el.value.close();
    }
  }
}

function handleEscape(event: KeyboardEvent) {
  if (
    closedByPolyfill &&
    (props.closedBy === "closerequest" || props.closedBy === "any")
  ) {
    // Prevents default and propagation to avoid issues like exiting browser fullscreen in Safari.
    event.stopPropagation();
    event.preventDefault();
    el.value.close();
  }
}
</script>
<style lang="scss">
@import "dialog-polyfill/dialog-polyfill.css";
</style>
